import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { MAX_FILESTACK_FILE_SIZE } from '@aline/common';
import { Apollo } from 'src/gql/generated';
import { useVersionAwareUpload } from 'src/helpers/docx-upload-target';
import { getVersionAwareUploadFailureMessage } from 'src/helpers/docx-upload-target/get-version-aware-upload-failure-message';
import { acceptedMimeTypePrefixes, acceptedMimeTypes } from 'src/helpers/integrations/filestack';
import { useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { v4 as uuid4 } from 'uuid';

import { BLOCKED_SIGNING_DROP_MESSAGE } from './document-panel-messages';

interface UseDocumentDropzoneOptions {
  attachedDocumentIds: string[];
  documentVariant: Apollo.DocumentVariant;
  workflowId: UUID;
  disabled?: boolean;
}

interface UseDocumentDropzoneReturn {
  dropzoneProps: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  isDragging: boolean;
  isUploading: boolean;
  versionDialog: JSX.Element;
}

function getUploadSuccessMessage({
  addedCount,
  updatedCount,
}: {
  addedCount: number;
  updatedCount: number;
}) {
  const added = addedCount
    ? `${addedCount} document${addedCount > 1 ? 's' : ''} added to workflow.`
    : '';
  let updated = '';
  if (updatedCount === 1) updated = 'Uploaded as latest version.';
  else if (updatedCount > 1) updated = `Uploaded ${updatedCount} files as latest versions.`;

  if (added && updated) return `${added} ${updated}`;
  return added || updated;
}

function isFileAccepted(file: File) {
  if (acceptedMimeTypes.has(file.type)) return true;
  return acceptedMimeTypePrefixes.some(prefix => file.type.startsWith(prefix));
}

export function useDocumentDropzone(
  options: UseDocumentDropzoneOptions
): UseDocumentDropzoneReturn {
  const { attachedDocumentIds, disabled = false, documentVariant, workflowId } = options;
  const dragCounter = useRef(0);
  const dispatch = useNotificationDispatch();
  const isUploadingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [addDocumentsToWorkflow] = Apollo.useAddDocumentsToWorkflowMutation();
  const { uploadFiles: uploadWithVersionMatch, versionDialog } = useVersionAwareUpload();

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (disabled || isUploadingRef.current) return;

      const validFiles = files.filter(
        file => file.size <= MAX_FILESTACK_FILE_SIZE && isFileAccepted(file)
      );
      const invalidFileCount = files.length - validFiles.length;
      if (invalidFileCount > 0) {
        dispatch({
          payload: {
            key: uuid4(),
            message: `${invalidFileCount} file${invalidFileCount > 1 ? 's were' : ' was'} skipped (unsupported format or too large).`,
            variant: 'warning',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      }

      if (!validFiles.length) return;

      isUploadingRef.current = true;
      setIsUploading(true);

      try {
        const { failedFiles, uploadedFiles } = await uploadWithVersionMatch(validFiles, {
          documentVariant,
        });
        const attachedIds = new Set(attachedDocumentIds);
        const documentIdsToAdd = uploadedFiles.flatMap(({ documentId }) =>
          attachedIds.has(documentId) ? [] : [documentId]
        );
        const updatedCount = uploadedFiles.filter(file => file.kind === 'version').length;

        let addedCount = 0;
        if (documentIdsToAdd.length) {
          try {
            await addDocumentsToWorkflow({
              variables: { input: { documentIds: documentIdsToAdd, workflowId } },
            });
            addedCount = documentIdsToAdd.length;
          } catch (error) {
            dispatch({
              payload: {
                key: uuid4(),
                message:
                  error instanceof Error
                    ? error.message
                    : 'Could not add documents to the workflow.',
                variant: 'error',
              },
              type: NotificationsActionType.PUSH_NOTIFICATION,
            });
          }
        }

        if (addedCount || updatedCount) {
          dispatch({
            payload: {
              key: uuid4(),
              message: getUploadSuccessMessage({ addedCount, updatedCount }),
              variant: 'success',
            },
            type: NotificationsActionType.PUSH_NOTIFICATION,
          });
        }

        const failureMessage = getVersionAwareUploadFailureMessage({
          failedCount: failedFiles.length,
          uploadedCount: uploadedFiles.length,
        });
        if (failureMessage)
          dispatch({
            payload: { key: uuid4(), message: failureMessage, variant: 'error' },
            type: NotificationsActionType.PUSH_NOTIFICATION,
          });
      } catch (error) {
        dispatch({
          payload: {
            key: uuid4(),
            message: error instanceof Error ? error.message : 'Upload failed.',
            variant: 'error',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      } finally {
        isUploadingRef.current = false;
        setIsUploading(false);
      }
    },
    [
      attachedDocumentIds,
      disabled,
      uploadWithVersionMatch,
      addDocumentsToWorkflow,
      workflowId,
      documentVariant,
      dispatch,
    ]
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;

      dragCounter.current += 1;
      if (e.dataTransfer?.items?.length) setIsDragging(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;

      e.dataTransfer.dropEffect = 'copy';
    },
    [disabled]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      dragCounter.current = 0;
      setIsDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (disabled && files.length > 0) {
        dispatch({
          payload: {
            key: uuid4(),
            message: BLOCKED_SIGNING_DROP_MESSAGE,
            variant: 'warning',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }
      uploadFiles(files);
    },
    [disabled, dispatch, uploadFiles]
  );

  useEffect(() => {
    const resetDragState = () => {
      dragCounter.current = 0;
      setIsDragging(false);
    };

    document.addEventListener('drop', resetDragState);
    return () => {
      document.removeEventListener('drop', resetDragState);
    };
  }, []);

  return {
    dropzoneProps: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
    isDragging,
    isUploading,
    versionDialog,
  };
}
